import { CogNeedTextField } from '@cogneed/react-utils';
import {
  Fade,
  FormControl,
  MenuItem,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useFreeResiliationFormulaContext from '../../../Contexts/FreeResiliationFormulaContext/useFreeResiliationFormulaContext';
import useSerieFreePriceContext from '../../../Contexts/SerieFreePriceContext/useSerieFreePriceContext';
import {
  BoxPrice, MobilePrice, PriceSerieFreeBoxVariable, PriceSerieFreeVariable, freeOffers,
} from '../../../../data';
import StyledFormControl from './styles';
import { Offer } from '../../../Contexts/FreeResiliationFormulaContext/types';

function NewOfferFormInner() {
  const { state, updateState } = useFreeResiliationFormulaContext();
  const { price: serieFreePrice, updatePrice: updateSerieFreePrice } = useSerieFreePriceContext();

  const allOffers: (BoxPrice | MobilePrice)[] = React.useMemo(() => ([
    ...freeOffers,
    { ...PriceSerieFreeVariable, firstYear: serieFreePrice },
    { ...PriceSerieFreeBoxVariable, firstYear: serieFreePrice },
  ]), [serieFreePrice]);
  const boxOffers = allOffers.filter((offer) => offer.__typename === 'BoxPrice');
  const mobileOffers = allOffers.filter((offer) => offer.__typename === 'MobilePrice');
  const offers = state.contractType === 'box' ? boxOffers : mobileOffers;

  const { i18n, t } = useTranslation('wysiwyg');
  const currencyFormat = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' });

  function formatOffer(offer: BoxPrice | MobilePrice) {
    return `${offer.name} (${currencyFormat.format(offer.firstYear)}${offer.secondYear ? ` / ${currencyFormat.format(offer.secondYear)}` : ''})`;
  }

  return (
    <StyledFormControl>
      <CogNeedTextField
        label={t('resiliation-fee.offer-section.offer.title')}
        select
        onChange={(e) => {
          const newOfferId = e.target.value;
          const newOffer = offers.find((p) => p.id === newOfferId);
          updateState((prev) => ({ ...prev, newOffer }));
        }}
        defaultValue={state?.newOffer?.id ?? ''}
        fullWidth
        SelectProps={{
          displayEmpty: true,
          MenuProps: {
            anchorOrigin: { vertical: 'bottom', horizontal: 2 },
            transformOrigin: { vertical: -6, horizontal: 2 },
          },
        }}
      >
        {offers.map((value) => (
          <MenuItem key={value.id} value={value.id}>
            {formatOffer(value)}
          </MenuItem>
        ))}
      </CogNeedTextField>
      <Fade in={state.newOffer?.id?.startsWith('serie-free')} mountOnEnter unmountOnExit>
        <FormControl className="price-update">
          <CogNeedTextField
            label={t('resiliation-fee.offer-section.customize.title')}
            type="number"
            sx={{ width: 200 }}
            defaultValue={offers.find((p) => p.id === 'serie-free')?.firstYear ?? ''}
            onChange={(e) => {
              const value = Number(e.target.value).valueOf();
              if (!Number.isNaN(value) && state.newOffer) {
                updateSerieFreePrice(value);
                updateState((prev) => ({
                  ...prev,
                  newOffer: { ...prev.newOffer as Offer, firstYear: value },
                }));
              }
            }}
            InputProps={{
              inputProps: { min: 0 },
              endAdornment: <Typography variant="body1">€</Typography>,
            }}
          />
        </FormControl>
      </Fade>
    </StyledFormControl>
  );
}

export default function NewOfferForm() {
  const { terminationFees, currentOfferCost } = useFreeResiliationFormulaContext();
  if (!terminationFees || !currentOfferCost) return null;
  return <NewOfferFormInner />;
}
